import { FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SPACING } from "src/_globalStore/redux/reducers/Theme/themeConstants";
import useWindowSize from "src/custom-hooks/useWindowSize/useWindowSize";
import { ListSortDirection } from "src/models/api/sort";
import { MailClassificationStatusRef } from "src/models/mail-classification-status";
import { MailInboxDTO } from "src/models/mail-inbox";
import { OutgoingMailFilter } from "src/models/outgoing-mail/outgoing-mail-filter";
import { OutgoingMailStatusRef } from "src/models/outgoing-mail-status";
import { CUSTOM_PAGINATION_ID } from "src/reusable-components/Table/CustomTablePagination/utils";
import { OutgoingMailApiService } from "src/services/outgoing-mail/outgoing-mail-api.service";
import { MAIL_CLASSIFICATION_STATUS, OUTGOING_MAIL_STATUSES } from "src/utils/constants/constants";
import { State } from "./interfaces";
import ManageOutgoingMailsList from "./ManageOutgoingMailsList/ManageOutgoingMailsList";
import { Container, TopHeader } from "./styles";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import useQuery from "../../custom-hooks/useQuery/useQuery";
import useSearchOptions from "../../custom-hooks/useSearchOptions/useSearchOptions";
import CustomTablePagination from "../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import AddOutgoingMail from "../AddOutgoingMail/AddOutgoingMail";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { useMailClassificationStatusQuickFilter } from "../Quickfilters/MailClassificationStatusQuickfilter";
import { MailInboxQuickfilter, useMailInboxQuickFilter } from "../Quickfilters/MailInboxQuickfilter";
import {
  MailInboxUploaderQuickfilter,
  useMailInboxUploaderQuickFilter,
} from "../Quickfilters/MailInboxUploaderQuickfilter";
import {
  OutgoingMailCombinedStatusQuickfilter,
  useOutgoingMailCombinedStatusQuickFilter,
} from "../Quickfilters/OutgoingMailCombinedStatusQuickfilter";
import { useOutgoingMailStatusQuickFilter } from "../Quickfilters/OutgoingMailStatusQuickfilter";
import { PeriodFromToQuickfilter, usePeriodFromToQuickFilter } from "../Quickfilters/PeriodFromToQuickFilter";

import QuickfiltersContainer from "../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import TableHeader from "../TableHeader/TableHeader";

const ManageOutgoingMailsContainer = () => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  const query = useQuery();
  const { t } = useTranslation();
  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
    totalItems: 0,
  });

  //#region mail inbox list
  //get the list of mail inboxes this user has access to their outgoing mails
  //this will be used to filter the mail inboxes this user can chose from the quickfilter
  const authorizedMailInboxesForIncoming: string[] =
    currentUserAuthorizations?.allowedMailInboxes
      ?.filter(
        (allowedMailInbox) =>
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadOutgoing) ||
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanClassifyOutgoing) ||
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanReadOutgoing) ||
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanProcessOutgoing)
      )
      .map((allowedMailInbox) => allowedMailInbox.key) ?? [];
  //#endregion

  const accessedViaLink = query.get("via");
  const DEFAULT_OUTGOING_MAIL_STATUS = OUTGOING_MAIL_STATUSES.Pending;
  const DEFAULT_CLASSIFICATION_MAIL_STATUS = MAIL_CLASSIFICATION_STATUS.ToBeClassified;

  //#region quickfilters
  const { mailInboxQuickFilter, setMailInboxQuickFilter, selectedMailInbox } = useMailInboxQuickFilter();
  const { outgoingMailStatusQuickFilter, setOutgoingMailStatusQuickFilter } = useOutgoingMailStatusQuickFilter(
    accessedViaLink === "pending" ? [DEFAULT_OUTGOING_MAIL_STATUS as OutgoingMailStatusRef] : []
  );
  const { mailClassificationStatusQuickFilter, setMailClassificationStatusQuickFilter } =
    useMailClassificationStatusQuickFilter(
      accessedViaLink === "unclassified" ? [DEFAULT_CLASSIFICATION_MAIL_STATUS as MailClassificationStatusRef] : []
    );
  const { mailInboxUploaderQuickFilter, setMailInboxUploaderQuickFilter } = useMailInboxUploaderQuickFilter();
  const { periodFromToQuickFilter, setPeriodFromToQuickFilter } = usePeriodFromToQuickFilter();
  const { outgoingMailCombinedStatusQuickFilter, setOutgoingMailCombinedStatusQuickFilter } =
    useOutgoingMailCombinedStatusQuickFilter();

  //#endregion

  const currentMailInboxAuth = currentUserAuthorizations?.allowedMailInboxes?.find(
    (mailInboxAuth) => mailInboxAuth.key === selectedMailInbox?.id
  )?.value;

  const canUploadForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadOutgoing) ?? false;
  const canClassifyForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanClassifyOutgoing) ?? false;
  const canReadForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanReadOutgoing) ?? false;
  const canProcessForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanProcessOutgoing) ?? false;

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions("");
  const { height } = useWindowSize();

  const loadData = useCallback(
    async (signal?: AbortSignal) => {
      if (selectedMailInbox) {
        setState((state) => ({
          ...state,
          loading: true,
        }));
        try {
          const periodFromDate = periodFromToQuickFilter.period?.startDate;
          const periodToDate = periodFromToQuickFilter.period?.endDate;

          const response = await new OutgoingMailApiService().loadItems(
            {
              mailInboxIds: [selectedMailInbox.id],
              statusIds: outgoingMailCombinedStatusQuickFilter.outgoingMailCombinedStatus.statusIds.filter((statusId) =>
                Object.values(OUTGOING_MAIL_STATUSES)
                  .flatMap((_status) => _status.id)
                  .includes(statusId)
              ),
              classificationStatusIds:
                outgoingMailCombinedStatusQuickFilter.outgoingMailCombinedStatus.statusIds.filter((statusId) =>
                  Object.values(MAIL_CLASSIFICATION_STATUS)
                    .flatMap((_status) => _status.id)
                    .includes(statusId)
                ),
              ...(periodFromDate &&
                periodToDate && {
                  fromDate: {
                    day: periodFromDate.getDate(),
                    month: periodFromDate.getMonth() + 1,
                    year: periodFromDate.getFullYear(),
                  },
                  toDate: {
                    day: periodToDate.getDate(),
                    month: periodToDate.getMonth() + 1,
                    year: periodToDate.getFullYear(),
                  },
                }),
              operatorOR:
                outgoingMailCombinedStatusQuickFilter.outgoingMailCombinedStatus.statusIds.length > 0
                  ? true
                  : undefined,
            } as OutgoingMailFilter,
            searchOptions.query,
            [
              {
                name: "createdAt",
                direction: ListSortDirection.Descending,
              },
              {
                name: "modifiedAt",
                direction: ListSortDirection.Ascending,
              },
            ],
            state.page,
            searchOptions.exactMatch,
            60
          );
          if (response.error) {
            setState((state) => ({
              ...state,
              error: response.error,
              items: [],
            }));
          } else {
            setState((state) => ({
              ...state,
              error: "",
              items: response.items,
              totalItems: response.totalItems,
            }));
          }
        } catch (error) {
          setState((state) => ({
            ...state,
            error: error as string,
          }));
        } finally {
          setState((state) => ({
            ...state,
            loading: false,
          }));
        }
      }
    },
    [
      selectedMailInbox,
      // outgoingMailStatusQuickFilter,
      // mailClassificationStatusQuickFilter,
      periodFromToQuickFilter,
      searchOptions.exactMatch,
      searchOptions.query,
      state.page,
      outgoingMailCombinedStatusQuickFilter,
    ]
  );

  useEffect(() => {
    const abortController = new AbortController();

    loadData(abortController.signal);

    return () => abortController.abort();
  }, [state.page]);

  useEffect(() => {
    const abortController = new AbortController();

    if (state.page.pageNumber === 0) {
      loadData(abortController.signal);
    } else {
      setState((state) => ({
        ...state,
        page: {
          ...state.page,
          pageNumber: 0,
        },
      }));
    }
    return () => abortController.abort();
  }, [
    selectedMailInbox,
    // outgoingMailStatusQuickFilter,
    // mailClassificationStatusQuickFilter,
    periodFromToQuickFilter,
    searchOptions.exactMatch,
    searchOptions.query,
    outgoingMailCombinedStatusQuickFilter,
  ]);

  const handleChangePageNumber = useCallback((pageNumber: number) => {
    setState((state) => ({
      ...state,
      page: {
        ...state.page,
        pageNumber,
      },
    }));
  }, []);

  const handleChangePageSize = useCallback((pageSize: number) => {
    setState((state) => ({
      ...state,
      page: {
        ...state.page,
        pageSize,
      },
    }));
  }, []);

  const currHeight =
    height -
    (75 + //add button height
      SPACING[9] * 2 +
      (document.getElementById("header")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById(`${CUSTOM_PAGINATION_ID}`)?.clientHeight ?? 0) +
      40);

  return (
    <Container>
      <TopHeader>
        {canUploadForTheCurrentMailInbox && (
          <AddOutgoingMail
            mailInbox={(selectedMailInbox as MailInboxDTO) ?? null}
            handleNotifyParent={loadData}
          />
        )}
        <MailInboxQuickfilter
          quickFilters={mailInboxQuickFilter}
          handleChangeQuickFilters={setMailInboxQuickFilter}
          customPropsFilter={{ ids: authorizedMailInboxesForIncoming }}
        />
      </TopHeader>

      <TableHeader
        searchOptions={searchOptions}
        handleChangeSearchOptions={handleChangeSearchOptions}
        quickFilters={
          <QuickfiltersContainer>
            <MailInboxUploaderQuickfilter
              quickFilters={mailInboxUploaderQuickFilter}
              handleChangeQuickFilters={setMailInboxUploaderQuickFilter}
              allUploaders={
                selectedMailInbox?.authorizedUsers
                  .filter((authUser) => authUser.canUploadOutgoing && authUser.user)
                  .map((authUser) => authUser.user!) ?? []
              }
            />
            <OutgoingMailCombinedStatusQuickfilter
              quickFilters={outgoingMailCombinedStatusQuickFilter}
              handleChangeQuickFilters={setOutgoingMailCombinedStatusQuickFilter}
            />
            <FormControlLabel
              label={"Show only mails requiring actions"}
              style={{ margin: "10px 0px" }}
              control={
                <Switch
                  size="small"
                  checked={
                    outgoingMailCombinedStatusQuickFilter.outgoingMailCombinedStatus.statusIds.length === 2 &&
                    outgoingMailCombinedStatusQuickFilter.outgoingMailCombinedStatus.statusIds.includes(
                      OUTGOING_MAIL_STATUSES.Pending.id
                    ) &&
                    outgoingMailCombinedStatusQuickFilter.outgoingMailCombinedStatus.statusIds.includes(
                      MAIL_CLASSIFICATION_STATUS.ToBeClassified.id
                    )
                  }
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setOutgoingMailCombinedStatusQuickFilter("outgoingMailCombinedStatus", {
                        statusIds: [OUTGOING_MAIL_STATUSES.Pending.id, MAIL_CLASSIFICATION_STATUS.ToBeClassified.id],
                      });
                    } else {
                      setOutgoingMailCombinedStatusQuickFilter("outgoingMailCombinedStatus", { statusIds: [] });
                    }
                  }}
                />
              }
            />
            {/* <OutgoingMailStatusQuickfilter
              quickFilters={outgoingMailStatusQuickFilter}
              handleChangeQuickFilters={setOutgoingMailStatusQuickFilter}
              {...(accessedViaLink === "pending" && {
                selectedValue: [DEFAULT_OUTGOING_MAIL_STATUS.id],
              })}
            />
            <MailClassificationStatusQuickfilter
              quickFilters={mailClassificationStatusQuickFilter}
              handleChangeQuickFilters={setMailClassificationStatusQuickFilter}
              {...(accessedViaLink === "unclassified" && {
                selectedValue: [DEFAULT_CLASSIFICATION_MAIL_STATUS.id],
              })}
            /> */}

            <PeriodFromToQuickfilter
              quickFilters={periodFromToQuickFilter}
              handleChangeQuickFilters={setPeriodFromToQuickFilter}
            />
          </QuickfiltersContainer>
        }
        titleElement={<Grid style={{ marginLeft: "20px" }}></Grid>}
      />
      {!state.loading && state.items.length === 0 && (
        <Grid style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
          <Typography>{`${t("common_words.no_items")}`}</Typography>
        </Grid>
      )}
      <>
        <Grid
          sx={{
            width: "100%",
            height: `${currHeight}px`,
            overflow: "auto",
            marginTop: "10px",
          }}
        >
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <ManageOutgoingMailsList
              items={state.items}
              handleNotifyParent={loadData}
              canUpload={canUploadForTheCurrentMailInbox}
              canClassify={canClassifyForTheCurrentMailInbox}
              canRead={canReadForTheCurrentMailInbox}
              canProcess={canProcessForTheCurrentMailInbox}
            />
          </LoadingOverlay>
        </Grid>

        <CustomTablePagination
          totalItems={state.totalItems}
          pageNumber={state.page.pageNumber}
          pageSize={state.page.pageSize}
          handleChangePageNumber={handleChangePageNumber}
          handleChangePageSize={handleChangePageSize}
        />
      </>
    </Container>
  );
};

export default ManageOutgoingMailsContainer;
